import { OBJECTS } from '../data/objects.js';

/**
 * Everything in the world that is not the building and not a person.
 *
 * An orange on a break room counter, a wine bottle in flight, the shards it
 * left, a scorpion, a table cart. They differ only by which row of the object
 * catalogue they point at, so every system that touches them — carrying,
 * throwing, perception, ownership — takes one code path.
 *
 * State is deliberately a small closed set:
 *   world    sitting somewhere, pickable
 *   held     in somebody's hands, no position of its own
 *   flying   in the air, owned by the throwing system
 *   gone     consumed, broken, or otherwise no longer a thing
 *
 * `claim` is which BEO has counted on this object existing. Taking a claimed
 * one is the same verb as taking an unclaimed one; the claim is what makes it
 * a shortfall afterwards.
 */

let nextId = 1;

export function createEntities() {
  let items = new Map();
  const listeners = new Set();

  function emit(what, entity) {
    for (const listener of listeners) listener(what, entity);
  }

  function spawn({ kind, position = { x: 0, y: 0, z: 0 }, variant = null, claim = null, state = 'world', data = {} }) {
    const definition = OBJECTS[kind];
    if (!definition) throw new Error(`no object kind "${kind}"`);
    const entity = {
      id: `e${nextId++}`,
      kind,
      variant,
      claim,
      state,
      position: { ...position },
      yaw: 0,
      data,
    };
    items.set(entity.id, entity);
    emit('spawn', entity);
    return entity;
  }

  return {
    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    spawn,
    get(id) {
      return items.get(id) ?? null;
    },
    get all() {
      return [...items.values()];
    },
    get count() {
      return items.size;
    },

    /** Everything in the world right now with a given tag. */
    tagged(tag, { state = 'world' } = {}) {
      return [...items.values()].filter(
        (entity) =>
          (state === null || entity.state === state) && OBJECTS[entity.kind].tags.includes(tag)
      );
    },

    /**
     * The nearest world entity to a point, filtered.
     *
     * Used by picking up, by wildlife, and by anything else that wants "the
     * thing in front of me". It does not know or care what the thing is.
     */
    /**
     * The nearest one, or with `score` (entity, distance) the lowest-scoring
     * one in reach: how the key picks the thing you are aiming at rather than
     * the one at your feet. `distance` is always the real distance.
     */
    nearest(position, { within = Infinity, tag = null, where = null, score = null } = {}) {
      let best = null;
      for (const entity of items.values()) {
        if (entity.state !== 'world') continue;
        if (tag && !OBJECTS[entity.kind].tags.includes(tag)) continue;
        if (where && !where(entity)) continue;
        const distance = Math.hypot(
          position.x - entity.position.x,
          position.z - entity.position.z
        );
        if (distance > within) continue;
        const value = score ? score(entity, distance) : distance;
        if (!best || value < best.value) best = { entity, distance, value };
      }
      return best ? { entity: best.entity, distance: best.distance } : null;
    },

    setState(id, state) {
      const entity = items.get(id);
      if (!entity) return null;
      entity.state = state;
      emit('state', entity);
      return entity;
    },

    move(id, position) {
      const entity = items.get(id);
      if (!entity) return null;
      entity.position.x = position.x;
      entity.position.y = position.y ?? entity.position.y;
      entity.position.z = position.z;
      return entity;
    },

    remove(id) {
      const entity = items.get(id);
      if (!entity) return null;
      entity.state = 'gone';
      items.delete(id);
      emit('remove', entity);
      return entity;
    },

    /** Everything a BEO has claimed, for the shortfall machinery. */
    claimedBy(beoId) {
      return [...items.values()].filter((entity) => entity.claim === beoId);
    },

    clear() {
      items = new Map();
      emit('clear', null);
    },

    toJSON: () => ({
      next: nextId,
      items: [...items.values()].map((entity) => ({
        id: entity.id,
        kind: entity.kind,
        variant: entity.variant,
        claim: entity.claim,
        state: entity.state,
        position: entity.position,
        data: entity.data,
      })),
    }),

    load(state) {
      items = new Map();
      nextId = state?.next ?? nextId;
      for (const saved of state?.items ?? []) {
        items.set(saved.id, { yaw: 0, ...saved, position: { ...saved.position } });
      }
      emit('clear', null);
    },
  };
}
